import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useI18n } from '../context/I18nContext';
import axios from 'axios';
import { Bell, Check, Gift, Package, Info } from 'lucide-react';
import { API } from '../config';


const typeIcons = {
  referral_tier: <Gift className="w-4 h-4 text-amber-500" />,
  module_status: <Package className="w-4 h-4 text-blue-500" />,
  default: <Info className="w-4 h-4 text-muted-foreground" />,
};

export const NotificationBell = () => {
  const { user, getToken } = useAuth();
  const { lang } = useI18n();
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ notifications: [], unread_count: 0 });
  const ref = useRef(null);

  const fetchNotifs = () => {
    const token = getToken();
    if (!token) return;
    axios.get(`${API}/notifications`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setData(r.data)).catch(() => {});
  };

  useEffect(() => { if (!user) return; fetchNotifs(); const i = setInterval(fetchNotifs, 30000); return () => clearInterval(i); }, [user]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }; document.addEventListener('mousedown', h); return () => document.removeEventListener('mousedown', h); }, []);

  const markAllRead = () => { const t = getToken(); if (!t) return; axios.post(`${API}/notifications/read`, {}, { headers: { Authorization: `Bearer ${t}` } }).then(() => fetchNotifs()); };
  const markRead = (id) => { const t = getToken(); if (!t) return; axios.post(`${API}/notifications/read`, { notification_id: id }, { headers: { Authorization: `Bearer ${t}` } }).then(() => fetchNotifs()); };

  if (!user) return null;

  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen(!open)} className="relative p-2 rounded-full text-muted-foreground hover:text-foreground hover:bg-muted transition-all" data-testid="notification-bell">
        <Bell className="w-4.5 h-4.5" />
        {data.unread_count > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-4 h-4 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center" data-testid="notification-badge">
            {data.unread_count > 9 ? '9+' : data.unread_count}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-2 w-80 rounded-xl bg-card border border-border shadow-2xl overflow-hidden z-50" data-testid="notification-dropdown">
          <div className="flex items-center justify-between px-4 py-3 border-b border-border">
            <span className="text-sm font-medium text-foreground">Notifications</span>
            {data.unread_count > 0 && (
              <button onClick={markAllRead} className="flex items-center gap-1 text-xs text-blue-500 hover:text-blue-400 transition-colors" data-testid="notification-mark-all-read">
                <Check className="w-3 h-3" />{lang === 'fr' ? 'Tout lire' : 'Mark all read'}
              </button>
            )}
          </div>
          <div className="max-h-72 overflow-y-auto">
            {data.notifications.length === 0 ? (
              <p className="text-muted-foreground text-sm text-center py-6" data-testid="notification-empty">{lang === 'fr' ? 'Aucune notification' : 'No notifications'}</p>
            ) : (
              data.notifications.map((n) => (
                <div key={n.id} onClick={() => { if (!n.read) markRead(n.id); if (n.link) setOpen(false); }}
                  className={`flex gap-3 px-4 py-3 border-b border-border/50 hover:bg-muted transition-colors cursor-pointer ${!n.read ? 'bg-blue-500/5' : ''}`}
                  data-testid={`notification-item-${n.id}`}>
                  <div className="mt-0.5 shrink-0">{typeIcons[n.type] || typeIcons.default}</div>
                  <div className="flex-1 min-w-0">
                    {n.link ? (
                      <Link to={n.link} className="text-sm text-foreground font-medium hover:text-blue-500 transition-colors block truncate">{n.title}</Link>
                    ) : (
                      <p className="text-sm text-foreground font-medium truncate">{n.title}</p>
                    )}
                    <p className="text-xs text-muted-foreground line-clamp-2">{n.message}</p>
                    <p className="text-[10px] text-muted-foreground/60 mt-1">{new Date(n.created_at).toLocaleDateString()}</p>
                  </div>
                  {!n.read && <span className="w-2 h-2 rounded-full bg-blue-500 mt-1.5 shrink-0" />}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};
